import { Checkbox } from 'antd';
import React, { ReactNode, useMemo } from 'react';
import { eventType, OP_STATUS, OPERATION_TYPE, PATHNAME } from '../../../../util/varibles/constants';
import { IActivityLog, IOperation, IOperations } from '../../../../util/varibles/interface';

type TOperation = IOperation['operation'];
type TInvoice = NonNullable<TOperation['invoices']>[number];

interface IProps {
    operation: TOperation
    activityLog: IActivityLog[]
    operations: IOperations
    vesselId: string
    value: string[]
    className?: string
    before?: ReactNode
    empty?: ReactNode
    showLinks?: boolean
    isEdit?: boolean
    onChange(value: string[], invoices: TInvoice[]): void
}

const getStartTime = (data: IOperation) => data.activity_log[0]?.est_start_time || 0;
const getFinishTime = (data: IOperation) => data.activity_log[data.activity_log.length - 1]?.est_start_time || 0;

const toInvoice = (operation: TOperation): TInvoice => ({
    operation_id: operation.id,
    tenant_id: operation.tenant_id,
    operation_code: operation.operation_code,
    operation_type: operation.operation_type,
    event_type: operation.operation_type === OPERATION_TYPE.EVENT
        ? operation.event_type
        : undefined,
});

const InvoiceRelatedSelector: React.FC<IProps> = ({
    operation,
    activityLog,
    operations,
    vesselId,
    value,
    className,
    before,
    empty = null,
    showLinks = false,
    isEdit = true,
    onChange,
}) => {
    const events = useMemo(() => {
        const treatmentStart = activityLog[0]?.est_start_time || 0;
        const treatmentFinish = activityLog[activityLog.length - 1]?.est_start_time || 0;
        const selectedEventIds = new Set(value.filter(id => id !== operation.id));
        const selectedEvents = Object.values(operations)
            .filter(item => selectedEventIds.has(item.operation.id)
                && item.operation.operation_type === OPERATION_TYPE.EVENT)
            .sort((a, b) => getStartTime(a) - getStartTime(b));
        const usedByAnotherOperation = new Set(
            Object.values(operations).flatMap(item => item.operation.id === operation.id
                || item.operation.operation_type === OPERATION_TYPE.EVENT
                ? []
                : (item.operation.invoices || []).map(invoice => invoice.operation_id))
        );
        const availableEvents = Object.values(operations).filter(item => {
            return item.operation.operation_type === OPERATION_TYPE.EVENT
                && item.operation.status !== OP_STATUS.NEW
                && item.vessel.id === vesselId
                && !usedByAnotherOperation.has(item.operation.id);
        });
        const before = availableEvents
            .filter(item => getFinishTime(item) <= treatmentStart)
            .sort((a, b) => getFinishTime(b) - getFinishTime(a))[0];
        const after = availableEvents
            .filter(item => getStartTime(item) >= treatmentFinish)
            .sort((a, b) => getStartTime(a) - getStartTime(b))[0];

        const rootEvents = selectedEvents.length >= 2
            ? selectedEvents
            : selectedEvents.length === 1
                ? getFinishTime(selectedEvents[0]) <= treatmentStart
                    ? [selectedEvents[0], after]
                    : [before, selectedEvents[0]]
                : [before, after];

        return rootEvents.filter((item, index, list): item is IOperation => {
            return !!item && list.findIndex(other => other?.operation.id === item.operation.id) === index;
        });
    }, [activityLog, operation.id, operations, value, vesselId]);

    if (events.length === 0)
        return <>{empty}</>;

    const handleChange = (selectedIds: string[]) => {
        const selectedEvents = events
            .filter(item => selectedIds.includes(item.operation.id))
            .map(item => toInvoice(item.operation));
        const invoices = selectedEvents.length > 0
            ? [toInvoice(operation), ...selectedEvents]
            : [];

        onChange(selectedIds, invoices);
    };

    return <Checkbox.Group
        className={className}
        value={value}
        disabled={!isEdit}
        onChange={values => handleChange(values as string[])}
    >
        {before}
        {events.map(item => {
            const { id, operation_code, event_type, tenant_id } = item.operation;
            return <Checkbox key={id} value={id}>
                {showLinks
                    ? <a
                        className='link'
                        href={`${PATHNAME.OPERATION}/${tenant_id}/${id}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        #{operation_code} | {eventType[event_type]?.name || 'Event'}
                    </a>
                    : <>#{operation_code} {eventType[event_type]?.name || 'Event'}</>}
            </Checkbox>;
        })}
    </Checkbox.Group>;
};

export default InvoiceRelatedSelector;
